import { type TariffCurrency } from '~/data/types/currencies'

export type LocaleCode = keyof typeof LOCALE_TO_CURRENCY
export type CurrencyCode = (typeof LOCALE_TO_CURRENCY)[LocaleCode]

// Ensures that the default currency code is supported by the billing system
const getDefaultCurrencyCode = (): TariffCurrency => {
  const envCurrency = process.env.NEXT_PUBLIC_DEFAULT_CURRENCY
  const supportedCurrencies: CurrencyCode[] = ['USD', 'EUR', 'SEK']

  if (envCurrency && supportedCurrencies.includes(envCurrency as CurrencyCode)) {
    return envCurrency as TariffCurrency
  }

  return 'USD' as const
}
export const DEFAULT_CURRENCY_CODE = getDefaultCurrencyCode()

/**
 * Helper: tolerant lookup that tries the exact locale and falls back to ENV default or USD. Currently only supports USD, EUR, or SEK, but can be extended to support other currencies.
 * @param locale - The locale to get the currency for.
 * @returns The currency code for the locale.
 * @example
 * getCurrencyForLocale('en-US') // returns 'USD'
 * getCurrencyForLocale('sv-SE') // returns 'SEK'
 * getCurrencyForLocale('fr-FR') // returns 'EUR'
 */
export default function getCurrencyForLocale(locale?: string): CurrencyCode {
  if (!locale) return DEFAULT_CURRENCY_CODE

  // 1) exact
  if (locale in LOCALE_TO_CURRENCY) {
    return LOCALE_TO_CURRENCY[locale as LocaleCode]
  }

  // 2) normalize casing like "sv-se" -> "sv-SE"
  const m = locale.match(/^([a-zA-Z]{2,3})(?:[-_](?:([A-Z]{2})|([a-zA-Z]+)))?/)
  if (!m) return DEFAULT_CURRENCY_CODE

  const lang = (m[1] || '').toLowerCase()
  const region = (m[2] || '').toUpperCase() // prefer region if present
  const norm = region ? `${lang}-${region}` : lang

  if (norm in LOCALE_TO_CURRENCY) {
    return LOCALE_TO_CURRENCY[norm as LocaleCode]
  }

  return DEFAULT_CURRENCY_CODE
}

/**
 * Maps a currency to the tariff subset if needed
 * @param currency - The currency to map
 * @param subset - The subset to map to ('tariff' or 'billing')
 * @returns The mapped currency
 */
export function mapCurrencyToTariffSubset(currency: string): TariffCurrency {
  const tariffMapping: Record<string, TariffCurrency> = {
    GBP: 'EUR', // British Pound -> Euro
    CAD: 'USD', // Canadian Dollar -> US Dollar
    AUD: 'USD', // Australian Dollar -> US Dollar
    PLN: 'EUR', // Polish Zloty -> Euro
    USD: 'USD', // Already supported
    EUR: 'EUR', // Already supported
    SEK: 'SEK', // Already supported
    CHF: 'EUR', // Swiss Franc -> Euro (tariff API supports EUR/USD/SEK only)
    NOK: 'EUR' // Norwegian Krone -> Euro (tariff API supports EUR/USD/SEK only)
  }

  return tariffMapping[currency] || DEFAULT_CURRENCY_CODE
}

const LOCALE_TO_CURRENCY = {
  // ——— North America ———
  'en-US': 'USD',
  'es-US': 'USD',

  'en-CA': 'CAD',
  'fr-CA': 'CAD',

  'es-MX': 'USD',

  // Greenland (geographically NA, ties to DK)
  'kl-GL': 'EUR',
  'da-GL': 'EUR',

  // ——— Europe ———
  'sv-SE': 'SEK',
  'fi-FI': 'EUR',
  'sv-FI': 'EUR',
  'nb-NO': 'NOK',
  'nn-NO': 'NOK',
  'da-DK': 'EUR',
  'is-IS': 'EUR',
  'fo-FO': 'EUR',

  'en-GB': 'GBP',
  'cy-GB': 'GBP',
  'gd-GB': 'GBP',
  'en-IE': 'EUR',
  'ga-IE': 'EUR',

  'de-DE': 'EUR',
  'de-AT': 'EUR',
  'de-CH': 'CHF',
  'de-LI': 'EUR',
  'de-LU': 'EUR',

  'fr-FR': 'EUR',
  'fr-BE': 'EUR',
  'fr-CH': 'CHF',
  'fr-LU': 'EUR',
  'fr-MC': 'EUR',

  'it-IT': 'EUR',
  'it-CH': 'CHF',
  'it-SM': 'EUR',
  'it-VA': 'EUR',

  'es-ES': 'EUR',
  'ca-ES': 'EUR',
  'eu-ES': 'EUR',
  'gl-ES': 'EUR',
  'pt-PT': 'EUR',
  'ca-AD': 'EUR',
  'fr-AD': 'EUR',
  'es-AD': 'EUR',
  'fr-MF': 'EUR',

  'nl-NL': 'EUR',
  'nl-BE': 'EUR',

  'el-GR': 'EUR',
  'bg-BG': 'EUR',
  'ro-RO': 'EUR',
  'hu-HU': 'EUR',
  'pl-PL': 'PLN',
  'cs-CZ': 'EUR',
  'sk-SK': 'EUR',
  'sl-SI': 'EUR',
  'hr-HR': 'EUR',
  'sr-RS': 'EUR',
  'bs-BA': 'EUR',
  'sq-AL': 'EUR',
  'mk-MK': 'EUR',
  'me-ME': 'EUR',

  'tr-TR': 'EUR',
  'el-CY': 'EUR',

  'et-EE': 'EUR',
  'lv-LV': 'EUR',
  'lt-LT': 'EUR',

  'mt-MT': 'EUR',

  'ru-RU': 'EUR',
  'uk-UA': 'EUR',
  'be-BY': 'EUR',
  'md-MD': 'EUR',

  // Switzerland (all official language variants)
  'rm-CH': 'CHF',

  // ——— Oceania ———
  'en-AU': 'AUD'
} as const

const ISO_4217_EXPONENTS: Record<string, number> = {
  USD: 2,
  EUR: 2,
  SEK: 2,
  GBP: 2,
  CAD: 2,
  AUD: 2,
  PLN: 2,
  CHF: 2,
  NOK: 2,
  JPY: 0,
  KRW: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3
}

const DEFAULT_EXPONENT = 2

function getExponent(currency?: string): number {
  if (!currency) return DEFAULT_EXPONENT
  return ISO_4217_EXPONENTS[currency.toUpperCase()] ?? DEFAULT_EXPONENT
}

/**
 * Converts an amount from **major** to **minor** currency units (ISO 4217).
 * For currencies with two decimal places (USD, EUR, GBP, SEK, …), minor = major × 10².
 * For zero-decimal currencies (JPY, KRW), minor = major.
 * Several APIs (prices, tariffs) expose money in minor units.
 *
 * @param amount - Amount in major units (e.g. dollars)
 * @param currency - Optional ISO 4217 currency code for correct exponent lookup
 * @returns Amount in minor units (e.g. cents)
 */
export function parseToSmallestDenomination(amount: number, currency?: string): number {
  const exponent = getExponent(currency)
  return Math.round(amount * Math.pow(10, exponent))
}

/**
 * Converts from **minor** to **major** units — inverse of {@link parseToSmallestDenomination}.
 *
 * @param amount - Amount in minor units (as returned by billing / price APIs)
 * @param currency - Optional ISO 4217 currency code for correct exponent lookup
 * @returns Amount in major units (for display and form fields)
 */
export function parseFromSmallestDenomination(amount: number, currency?: string): number {
  const exponent = getExponent(currency)
  return amount / Math.pow(10, exponent)
}
